// src/utils/act/actStructure.js
//
// ACT shares the Plan system with SAT but is structured differently: it has
// FOUR sections (English, Math, Reading, Science) and only ONE level of
// categories per section (no SAT-style subcategories). Plan items are therefore
// the ACT categories within each section. These helpers mirror the logic in
// components/admin/act/report/ACTSubcategoryBreakdown.js so the plan and the
// report agree.

export const ACT_SECTION_ORDER = ["English", "Math", "Reading", "Science"];

// Rx-hour multiplier per section (matches the ACT report breakdown).
export const ACT_RX_MULT = { English: 2, Reading: 2, Science: 2, Math: 1 };

export const normalize = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// ACT correct answers may be delimited by comma / space / slash / pipe.
export function splitCorrect(val) {
  return String(val || "")
    .split(/[,\s/|]+/)
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);
}

export function pct(c, t) {
  if (!t || t === 0) return null;
  return Math.round((c / t) * 100);
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/** Recommended hours for a category given its accuracy (same formula as report). */
export function rxHoursFor(section, rawPct) {
  if (rawPct == null) return 0;
  const mult = ACT_RX_MULT[section] ?? 2;
  const missed = clamp(100 - rawPct, 0, 100);
  const buckets = Math.ceil(missed / 10);
  return mult * buckets;
}

// ---------------------------------------------------------------------------
// Section timing
// ---------------------------------------------------------------------------
// Two ACT paper formats are in circulation and they are paced differently:
//
//   classic   English 75 Q / 45 min, Math 60 / 60, Reading 40 / 35, Science 40 / 35
//   enhanced  English 50 Q / 35 min, Math 45 / 50, Reading 36 / 40, Science 40 / 40
//
// The test-taker used to hard-code the enhanced clock, which gave the classic
// (215-question) forms far too little time. The limit is derived from the
// question counts stored with the test, so newly uploaded forms pace correctly.
export const ACT_BREAK_SECONDS = 10 * 60;

export const ACT_FORMAT_PROFILES = {
  classic: {
    label: "Classic ACT",
    sections: {
      English: { questions: 75, minutes: 45 },
      Math: { questions: 60, minutes: 60 },
      Reading: { questions: 40, minutes: 35 },
      Science: { questions: 40, minutes: 35 },
    },
  },
  enhanced: {
    label: "Enhanced ACT",
    sections: {
      English: { questions: 50, minutes: 35 },
      Math: { questions: 45, minutes: 50 },
      Reading: { questions: 36, minutes: 40 },
      Science: { questions: 40, minutes: 40 },
    },
  },
};

/** The classic form is the only one with a 75-question English section. */
export function detectACTProfile(counts = {}) {
  return Number(counts?.English) >= 60 ? "classic" : "enhanced";
}

/**
 * Resolve the question count and time limit for every section of one test.
 *
 * @param {object} opts
 * @param {object} [opts.counts]    question counts keyed by section
 * @param {object} [opts.overrides] optional `timing` value from the test
 *        document: { profile?: 'classic'|'enhanced', minutes?: { English: 45 } }
 * @returns {Record<string, { count: number, minutes: number, seconds: number }>}
 */
export function resolveACTSectionPlan({ counts = {}, overrides = {} } = {}) {
  const profileKey = ACT_FORMAT_PROFILES[overrides?.profile]
    ? overrides.profile
    : detectACTProfile(counts);
  const profile = ACT_FORMAT_PROFILES[profileKey];
  const minutesOverride = overrides?.minutes || {};

  const plan = {};
  for (const section of ACT_SECTION_ORDER) {
    const preset = profile.sections[section];
    const rawCount = Number(counts?.[section]);
    const count = Number.isFinite(rawCount) && rawCount > 0 ? rawCount : preset.questions;
    const explicitMinutes = Number(minutesOverride[section]);
    const minutes =
      Number.isFinite(explicitMinutes) && explicitMinutes > 0
        ? explicitMinutes
        : Math.max(1, Math.round((count * preset.minutes) / preset.questions));
    plan[section] = { count, minutes, seconds: minutes * 60, profile: profileKey };
  }

  plan.BREAK = {
    count: 0,
    minutes: ACT_BREAK_SECONDS / 60,
    seconds: ACT_BREAK_SECONDS,
    profile: profileKey,
  };
  return plan;
}
